// Copyright © 2019-2026 Focal Systems. All rights reserved.

import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { useSettingDetailQuery } from "@/api/hooks/use-settings";
import { PageHeader } from "@/components/layout/page-header";
import { IdPill } from "@/components/shared/id-pill";
import { KeyChip } from "@/components/shared/key-chip";
import { DetailPageSkeleton, PageProgressBar } from "@/components/shared/page-loading";
import { RequirePermission } from "@/components/shared/require-permission";
import { Tabs, TabsContent, TabsIndicator, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { SettingActivityTab } from "@/features/settings/setting-activity-tab";
import { SettingChainsTab } from "@/features/settings/setting-chains-tab";
import { SettingInfoTab } from "@/features/settings/setting-info-tab";
import { SettingStoresTab } from "@/features/settings/setting-stores-tab";
import { useAuth } from "@/lib/auth/auth-provider";
import { PAGE_PERMISSIONS } from "@/lib/auth/page-permissions";
import { useDebugMode } from "@/lib/debug-mode";

export const Route = createFileRoute("/_authenticated/settings/$settingName")({
  validateSearch: z.object({ t: z.string().optional(), f: z.string().optional() }),
  component: () => (
    <RequirePermission permission={PAGE_PERMISSIONS.settings}>
      <SettingDetailPage />
    </RequirePermission>
  ),
});

function SettingDetailPage() {
  const { settingName } = Route.useParams();
  const { t: tab, f } = Route.useSearch();
  const { hasPermission } = useAuth();
  const { debugMode } = useDebugMode();
  const canViewChains = hasPermission(PAGE_PERMISSIONS.settingsChains);
  const canViewStores = hasPermission(PAGE_PERMISSIONS.settingsStores);
  // Activity log is work-in-progress — debug-mode only (in addition to the view permission).
  const canViewActivity = hasPermission(PAGE_PERMISSIONS.settingsActivity) && debugMode;
  const tabAllowed: Record<string, boolean> = {
    info: true,
    chains: canViewChains,
    stores: canViewStores,
    activity: canViewActivity,
  };
  const requestedTab = tab ?? "info";
  const activeTab = tabAllowed[requestedTab] ? requestedTab : "info";
  const navigate = useNavigate();
  const { t } = useTranslation("back-office");
  const { data: setting, isLoading } = useSettingDetailQuery(settingName);
  function handleTabChange(tab: string) {
    navigate({
      to: "/settings/$settingName",
      params: { settingName },
      search: tab === "info" ? {} : { t: tab },
      replace: true,
    });
  }

  const displayName = setting?.name ?? settingName;
  const breadcrumbs = useMemo(() => [{ label: t("settings.title"), to: "/settings" }], [t]);

  if (isLoading) {
    return (
      <div>
        <PageProgressBar />
        <DetailPageSkeleton />
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={displayName}
        subtitle={
          <>
            {setting?.settingId && <IdPill id={setting.settingId} />}
            <KeyChip label={settingName} />
          </>
        }
        breadcrumbs={breadcrumbs}
      />
      <div>
        <Tabs value={activeTab} onValueChange={handleTabChange} className="gap-0">
          <div className="w-full overflow-x-auto overflow-y-hidden pl-5 pr-6 pt-6">
            <TabsList>
              <TabsIndicator />
              <TabsTrigger value="info">{t("tabs.info")}</TabsTrigger>
              {canViewChains && <TabsTrigger value="chains">{t("tabs.chains")}</TabsTrigger>}
              {canViewStores && <TabsTrigger value="stores">{t("tabs.stores")}</TabsTrigger>}
              {canViewActivity && <TabsTrigger value="activity">{t("tabs.activityLog")}</TabsTrigger>}
            </TabsList>
          </div>
          <TabsContent value="info" className="space-y-4">
            <SettingInfoTab settingName={settingName} />
          </TabsContent>
          {canViewChains && (
            <TabsContent value="chains" className="space-y-4">
              <SettingChainsTab settingName={settingName} initialFilters={activeTab === "chains" ? f : undefined} />
            </TabsContent>
          )}
          {canViewStores && (
            <TabsContent value="stores" className="space-y-4">
              <SettingStoresTab settingName={settingName} initialFilters={activeTab === "stores" ? f : undefined} />
            </TabsContent>
          )}
          {canViewActivity && (
            <TabsContent value="activity" className="space-y-4">
              <SettingActivityTab settingName={settingName} initialFilters={activeTab === "activity" ? f : undefined} />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
